import React, { Component, ErrorInfo, ReactNode } from "react";

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('Error caught in boundary:', error);
    return { 
      hasError: true, 
      error: error instanceof Error ? error : new Error(JSON.stringify(error)) 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Ensure error is properly formatted before logging
    const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    console.error("Uncaught error:", {
      message: errorMessage,
      stack: errorStack,
      componentStack: errorInfo.componentStack
    });
    // TODO: Log error to an external service (e.g., Sentry, LogRocket)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="flex flex-col items-center justify-center w-screen h-screen bg-[#292929] text-white p-8">
          <div className="max-w-2xl w-full bg-[#383838] rounded-lg shadow-xl p-8">
            <h1 className="text-3xl font-bold mb-4">Something went wrong.</h1>
            <p className="text-lg mb-6">We&apos;re sorry for the inconvenience. Please try refreshing the page or contact support if the issue persists.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-mainPink hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out"
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-mainPink hover:text-blue-300">Error Details</summary>
                <pre className="mt-2 p-4 bg-gray-700 rounded overflow-x-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
